import clsx from "clsx";
import { EditDiff } from "@/components/ui/edit-diff";
import {
  REVIEW_DIFF_ADDED,
  REVIEW_DIFF_REMOVED,
} from "@/components/review/reviewSurfaces";
import { RATING_TILE_CLASS } from "@/components/case-checking/problemStatementUi";
import { callLabel } from "@/components/case-checking/previousReviewDiff";
import { TEXT_MUTED, TEXT_ROW_TITLE } from "@/components/ui/surface/surfaceTokens";
import type { DiffItem, DiffItemKind, DiffSection, DiffSide } from "@/services/api/case-checking";

const MARKER_TONE: Record<DiffItemKind, string> = {
  added: "text-emerald-600 dark:text-emerald-400",
  removed: "text-rose-600 dark:text-rose-400",
  changed: "text-amber-600 dark:text-amber-400",
};

const MARKER_SR: Record<DiffItemKind, string> = {
  added: "Added",
  removed: "Removed",
  changed: "Changed",
};

const MARKER_CHAR: Record<DiffItemKind, string> = {
  added: "+",
  removed: "−",
  changed: "~",
};

const CALL_TONE: Record<string, string> = {
  pass: RATING_TILE_CLASS.pass,
  fail: RATING_TILE_CLASS.fail,
  unclear: RATING_TILE_CLASS.unclear,
  na: RATING_TILE_CLASS.na,
  yes: RATING_TILE_CLASS.pass,
  no: RATING_TILE_CLASS.fail,
  agree: RATING_TILE_CLASS.pass,
  disagree: RATING_TILE_CLASS.fail,
  excluded: "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:ring-slate-500/25",
  deleted: "bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-500/30",
  created: "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30",
  edited: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-500/30",
  suitable: "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30",
  "dev-needs": "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30",
  unsuitable: "bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-500/30",
};

function DiffCallPill({ call, struck }: { call: string | null; struck?: boolean }) {
  if (call == null) return null;
  const tone = CALL_TONE[call] ?? "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-500/30";
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        tone,
        struck && REVIEW_DIFF_REMOVED,
      )}
      data-testid="diff-call-pill"
    >
      {callLabel(call)}
    </span>
  );
}

function DiffTextBlock({
  kind,
  before,
  after,
}: {
  kind: DiffItemKind;
  before: string;
  after: string;
}) {
  if (kind === "changed" && (before || after)) {
    const beforeTrim = before.trim();
    const afterTrim = after.trim();
    if (beforeTrim && afterTrim && beforeTrim !== afterTrim) {
      return (
        <div className="space-y-2" data-testid="edit-diff">
          <p className={clsx("text-sm leading-relaxed whitespace-pre-wrap", REVIEW_DIFF_REMOVED)} data-testid="edit-diff-removed">
            {before}
          </p>
          <p className={clsx("text-sm leading-relaxed whitespace-pre-wrap", REVIEW_DIFF_ADDED)} data-testid="edit-diff-added">
            {after}
          </p>
        </div>
      );
    }
    return <EditDiff mode="text" before={before} after={after} />;
  }
  if (kind === "added" && after) {
    return <p className={clsx("text-sm leading-relaxed", REVIEW_DIFF_ADDED)}>{after}</p>;
  }
  if (kind === "removed" && before) {
    return <p className={clsx("text-sm leading-relaxed", REVIEW_DIFF_REMOVED)}>{before}</p>;
  }
  return null;
}

function DiffCallBlock({
  kind,
  current,
  selected,
}: {
  kind: DiffItemKind;
  current: DiffSide | null;
  selected: DiffSide | null;
}) {
  if (kind === "changed" && current?.call != null && selected?.call != null) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 text-sm" data-testid="diff-call-transition">
        <DiffCallPill call={current.call} />
        <span className={TEXT_MUTED} aria-label="becomes">→</span>
        <DiffCallPill call={selected.call} />
      </div>
    );
  }
  if (kind === "added" && selected?.call != null) {
    return <DiffCallPill call={selected.call} />;
  }
  if (kind === "removed" && current?.call != null) {
    return <DiffCallPill call={current.call} struck />;
  }
  return null;
}

function diffTextBefore(item: DiffItem): string {
  const notes = item.current?.notes?.trim() ?? "";
  const summary = item.current?.summary?.trim() ?? "";
  return notes || summary;
}

function diffTextAfter(item: DiffItem): string {
  const notes = item.selected?.notes?.trim() ?? "";
  const summary = item.selected?.summary?.trim() ?? "";
  return notes || summary;
}

export function DiffRow({ item, showCall }: { item: DiffItem; showCall: boolean }) {
  const hasCall = showCall && (item.current?.call != null || item.selected?.call != null);

  return (
    <li
      className="flex gap-2 py-2"
      data-testid={`diff-row-${item.kind}`}
    >
      <span className={clsx("w-4 shrink-0 font-mono text-sm font-semibold", MARKER_TONE[item.kind])} aria-hidden>
        {MARKER_CHAR[item.kind]}
      </span>
      <span className="sr-only">{MARKER_SR[item.kind]}</span>
      <div className="min-w-0 flex-1 space-y-1">
        <p className={TEXT_ROW_TITLE}>{item.label}</p>
        {hasCall ? (
          <DiffCallBlock kind={item.kind} current={item.current} selected={item.selected} />
        ) : null}
        <DiffTextBlock kind={item.kind} before={diffTextBefore(item)} after={diffTextAfter(item)} />
      </div>
    </li>
  );
}

function sectionChangeCounts(section: DiffSection): { added: number; removed: number; changed: number } {
  let added = 0;
  let removed = 0;
  let changed = 0;
  for (const item of section.items) {
    if (item.kind === "added") added += 1;
    else if (item.kind === "removed") removed += 1;
    else if (item.kind === "changed") changed += 1;
  }
  return { added, removed, changed };
}

function groupItems(items: DiffItem[]): { label: string | null; items: DiffItem[] }[] {
  const groups: { label: string | null; items: DiffItem[] }[] = [];
  const indexByLabel = new Map<string | null, number>();
  for (const item of items) {
    const key = item.group_label;
    let index = indexByLabel.get(key);
    if (index === undefined) {
      index = groups.length;
      indexByLabel.set(key, index);
      groups.push({ label: key, items: [] });
    }
    groups[index].items.push(item);
  }
  return groups;
}

export function DiffSection({
  title,
  section,
  groupByGroupLabel = false,
  showCall,
}: {
  title: string;
  section: DiffSection;
  groupByGroupLabel?: boolean;
  showCall: boolean;
}) {
  if (section.items.length === 0) return null;

  const counts = sectionChangeCounts(section);
  const countParts: string[] = [];
  if (counts.added) countParts.push(`+${counts.added}`);
  if (counts.removed) countParts.push(`−${counts.removed}`);
  if (counts.changed) countParts.push(`~${counts.changed}`);

  const groups = groupByGroupLabel ? groupItems(section.items) : [{ label: null, items: section.items }];

  return (
    <section className="space-y-1" data-testid="diff-section">
      <h4 className={clsx(TEXT_ROW_TITLE, "flex flex-wrap items-baseline gap-2")}>
        <span>{title}</span>
        {countParts.length > 0 ? (
          <span className={clsx(TEXT_MUTED, "text-xs font-normal")}>{countParts.join(" ")}</span>
        ) : null}
      </h4>
      <ul className="divide-y divide-slate-200/80 dark:divide-slate-700/60">
        {groups.map((group) => (
          <li key={group.label ?? "_ungrouped"} className="list-none">
            {group.label ? (
              <p className={clsx(TEXT_MUTED, "py-1 text-xs font-medium uppercase tracking-wide")}>{group.label}</p>
            ) : null}
            <ul>
              {group.items.map((item) => (
                <DiffRow key={item.key} item={item} showCall={showCall} />
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {section.unchanged_count > 0 ? (
        <p className={clsx(TEXT_MUTED, "text-xs")} data-testid="diff-section-unchanged">
          {section.unchanged_count} unchanged
        </p>
      ) : null}
    </section>
  );
}
